const DEFAULT_PRIMARY_COLOR = [37, 99, 235];

const clampColor = (value) => Math.max(0, Math.min(255, Math.round(value)));

const hasSufficientContrastOnWhite = ([red, green, blue]) => {
  const channel = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };
  const luminance = channel(red) * 0.2126 + channel(green) * 0.7152 + channel(blue) * 0.0722;
  return 1.05 / (luminance + 0.05) >= 4.5;
};

const readableHeaderColor = (color) => {
  if (hasSufficientContrastOnWhite(color)) return color;
  // Garde la teinte de la marque mais l'assombrit pour les titres blancs.
  return color.map((value) => clampColor(value * 0.55));
};

/**
 * Obtient une couleur de marque à partir du logo actif de l'entreprise.
 * Les pixels transparents, blancs, gris et très foncés sont ignorés afin
 * d'éviter qu'un fond ou du texte noir devienne la couleur du document.
 */
export const getDominantLogoColor = (logoUrl) => new Promise((resolve) => {
  if (!logoUrl || typeof Image === 'undefined') {
    resolve(null);
    return;
  }

  const image = new Image();
  image.crossOrigin = 'anonymous';

  image.onload = () => {
    try {
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(image, 0, 0, size, size);

      const { data } = context.getImageData(0, 0, size, size);
      const colors = new Map();

      for (let index = 0; index < data.length; index += 16) {
        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const alpha = data[index + 3];
        const maximum = Math.max(red, green, blue);
        const minimum = Math.min(red, green, blue);
        const saturation = maximum === 0 ? 0 : (maximum - minimum) / maximum;

        if (alpha < 160 || maximum < 45 || minimum > 225 || saturation < 0.22) continue;

        const key = [red, green, blue].map((value) => Math.round(value / 24) * 24).join(',');
        const existing = colors.get(key) || { weight: 0, red: 0, green: 0, blue: 0 };
        const weight = 1 + saturation * 4;
        existing.weight += weight;
        existing.red += red * weight;
        existing.green += green * weight;
        existing.blue += blue * weight;
        colors.set(key, existing);
      }

      let selected = null;
      colors.forEach((color) => {
        if (!selected || color.weight > selected.weight) selected = color;
      });

      resolve(selected
        ? [
          clampColor(selected.red / selected.weight),
          clampColor(selected.green / selected.weight),
          clampColor(selected.blue / selected.weight)
        ]
        : null);
    } catch (error) {
      resolve(null);
    }
  };

  image.onerror = () => resolve(null);
  image.src = logoUrl;
});

export const getPdfBrand = async ({ companyName, logoUrl }) => ({
  companyName: companyName?.trim() || 'Votre entreprise',
  primaryColor: readableHeaderColor((await getDominantLogoColor(logoUrl)) || DEFAULT_PRIMARY_COLOR)
});
